import { ADVENTURE_REWARD_ICONS } from "../../../adventure/adventure_box_rewards.js";
import { rewardAmountText } from "../../../common/reward_format.js";
import { ImgID } from "../../../image_loader/img_id.js";
import { ImagesHolder } from "../../../image_loader/images_holder.js";
import { NineSliceDrawer } from "../../../image_loader/nine_slicer/nine_slice_drawer.js";
import { TournamentService, type TournamentProgressReward } from "../../../tournament/tournament_service.js";
import { ColorScheme } from "../../common_ui_elements/color_scheme.js";
import { TextStyleScheme } from "../../common_ui_elements/text_style_scheme.js";
import { UIElement } from "../../common_ui_elements/ui_element.js";

type Step =
  | { kind: "wait"; duration: number }
  | { kind: "fade"; from: number; to: number; duration: number }
  | { kind: "fill"; from: number; to: number; duration: number }
  | { kind: "fly"; duration: number };

/**
 * One-row tournament bar for the menu, shown only when the crowns earned since
 * the menu last saw them reach the next reward (29 → 30 of 30; 25 of 30 shows
 * nothing). It fades in, fills from the start of the segment to the end, the
 * reward floats up and fades out, then the bar fades out too. Crowns past the
 * goal (29 + 3 = 32) count towards the next reward; there is no transition to
 * it. It does not take taps.
 */
export class TournamentMenuProgress extends UIElement
{
  public static readonly DESIGN_W = 215;
  public static readonly DESIGN_H = 114;

  private static readonly SEEN_CROWNS_KEY = "bb_tournament_menu_seen_crowns_v1";
  private static readonly PILL_H = 64;
  private static readonly PILL_PAD = 6;
  private static readonly PILL_BOTTOM_PAD = 5;
  private static readonly REWARD_ICON = 96;
  private static readonly REWARD_RISE = 16;
  private static readonly REWARD_FLY_RISE = 230;

  private static readonly FILL_COLOR = "#2C3DAF";

  private static readonly START_DELAY_MS = 650;
  private static readonly FADE_IN_MS = 250;
  // Whole bar, start to end (including the stretch under the icon).
  private static readonly FILL_MS = 350;
  private static readonly BEFORE_FLY_MS = 120;
  private static readonly FLY_MS = 450;
  private static readonly FADE_OUT_MS = 350;

  private rewards: TournamentProgressReward[] = [];
  private thresholds: number[] = [ 0 ];
  private readonly can_play: () => boolean;
  private readonly on_reward: ( reward: TournamentProgressReward ) => void;

  // Shown state.
  private segment = 0;
  private alpha = 0;
  private fly = 0;
  // Width of the blue fill as a share of the track. The fill step animates
  // it in one motion to the end of the track, under the icon too (not a
  // separate, faster step there).
  private fill_frac = 0;

  private steps: Step[] = [];
  private step_started_at = -1;
  private anim_active = false;
  private target_crowns = 0;
  private last_update_at = -1;

  /**
   * can_play: false while something covers the menu; the replay waits (and
   * pauses) until it is clear. on_reward: called once the bar has faded out
   * after reaching a reward (the menu opens the reward overlay).
   */
  public constructor( ctx: CanvasRenderingContext2D, x: number, y: number, can_play: () => boolean,
    on_reward: ( reward: TournamentProgressReward ) => void )
  {
    super(
      ctx,
      x,
      y,
      LayoutScaler.widthFromDesign( TournamentMenuProgress.DESIGN_W ),
      LayoutScaler.heightFromDesign( TournamentMenuProgress.DESIGN_H ),
      false,
      false
    );
    this.setClickable( false );
    this.can_play = can_play;
    this.on_reward = on_reward;

    this.plan();
  }

  public name(): string { return "TournamentMenuProgress"; }

  public isAnimating(): boolean
  {
    return this.anim_active;
  }

  /** The reward overlay is taking over: drop the replay and count the crowns as seen. */
  public skip(): void
  {
    this.anim_active = false;
    this.steps = [];
    this.alpha = 0;
    this.step_started_at = -1;
    TournamentMenuProgress.saveSeenCrowns( TournamentService.getCrowns() );
    this.setShouldBeRendered();
  }

  /** Debug / crowns earned while in the menu: replay the gain since the last shown value. */
  public refresh(): void
  {
    if ( !this.anim_active )
      this.plan();
  }

  private plan(): void
  {
    this.rewards = TournamentService.isActive() ? TournamentService.getProgressRewards() : [];
    this.thresholds = [ 0, ...this.rewards.map( reward => reward.crowns ) ];
    if ( !this.rewards.length ) return;
    const crowns = TournamentService.getCrowns();
    const seen = TournamentMenuProgress.loadSeenCrowns();
    // No record yet, or more than the player has now (a new tournament, a
    // reset): the crowns counted so far are all new.
    const from = seen === null || seen > crowns ? 0 : seen;
    this.target_crowns = crowns;

    const k = this.segmentOf( from );
    const last = this.rewards.length;
    // Only a reached reward is worth showing; below it the crowns just count
    // towards the next time (seen moves on, 25 → 31 still crosses 30).
    if ( from >= crowns || k >= last || crowns < this.thresholds[k + 1] || this.rewards[k].claimed )
    {
      TournamentMenuProgress.saveSeenCrowns( crowns );
      return;
    }

    this.segment = k;
    this.fill_frac = 0;
    this.fly = 0;
    this.alpha = 0;

    this.steps = [
      { kind: "wait", duration: TournamentMenuProgress.START_DELAY_MS },
      { kind: "fade", from: 0, to: 1, duration: TournamentMenuProgress.FADE_IN_MS },
      { kind: "fill", from: 0, to: 1, duration: TournamentMenuProgress.FILL_MS },
      { kind: "wait", duration: TournamentMenuProgress.BEFORE_FLY_MS },
      { kind: "fly", duration: TournamentMenuProgress.FLY_MS },
      { kind: "fade", from: 1, to: 0, duration: TournamentMenuProgress.FADE_OUT_MS },
    ];

    this.anim_active = true;
    this.step_started_at = -1;
  }

  /** Index of the segment the given crowns are in (thresholds[k] <= crowns < thresholds[k + 1]). */
  private segmentOf( crowns: number ): number
  {
    let k = 0;
    while ( k < this.thresholds.length - 1 && crowns >= this.thresholds[k + 1] )
      k++;
    return k;
  }

  // Lets the menu's foreground cache know it must rebuild once art arrives.
  public getImage(): ImageBitmap | null
  {
    for ( const id of [ ImgID.UI_TournamentMiniPbBg, ImgID.UI_TournamentMiniPbBase ] )
    {
      if ( !ImagesHolder.getLoadedImage( id ) )
        return null;
    }
    return ImagesHolder.getLoadedImage( ImgID.UI_TournamentMiniPbBg );
  }

  protected override updateBasic( now: number ): void
  {
    super.updateBasic( now );

    const dt = this.last_update_at < 0 ? 0 : now - this.last_update_at;
    this.last_update_at = now;

    if ( !this.anim_active )
      return;

    // Hold the timeline while the menu is covered (result overlay, dialogs,
    // the cart driving in), so the player actually sees the gain.
    if ( !this.can_play() )
    {
      if ( this.step_started_at >= 0 )
        this.step_started_at += dt;
      return;
    }

    this.setShouldBeRendered();
    this.advance( now );
  }

  private advance( now: number ): void
  {
    // Several short steps may end within one frame.
    for ( let guard = 0; guard < 8; guard++ )
    {
      const step = this.steps[0];
      if ( !step )
      {
        this.anim_active = false;
        const reward = this.rewards[this.segment];
        if ( reward )
          this.on_reward( reward );
        return;
      }

      if ( this.step_started_at < 0 )
        this.step_started_at = now;

      const t = Math.min( 1, ( now - this.step_started_at ) / step.duration );

      if ( step.kind === "fade" )
        this.alpha = step.from + ( step.to - step.from ) * t;
      else if ( step.kind === "fill" )
        this.fill_frac = step.from + ( step.to - step.from ) * easeInOutQuad( t );
      else if ( step.kind === "fly" )
        this.fly = t;

      if ( t < 1 )
        return;

      this.steps.shift();
      // The reward counts as seen once it has flown away; leaving the menu
      // before that plays the whole thing again next time.
      if ( step.kind === "fly" )
        TournamentMenuProgress.saveSeenCrowns( this.target_crowns );
      this.step_started_at = this.steps.length > 0 ? Math.min( now, this.step_started_at + step.duration ) : -1;
    }
  }

  // ── rendering ────────────────────────────────────────────────

  public render(): void
  {
    if ( !this.shouldBeRendered() )
      return;

    this.startRender();

    if ( this.alpha <= 0.01 )
    {
      this.finishRender();
      return;
    }

    const ctx = this.ctx;
    ctx.globalAlpha *= this.alpha;
    const w = this.rect.getW();
    const h = this.rect.getH();
    const left = -w / 2;

    const pill_h = LayoutScaler.heightFromDesign( TournamentMenuProgress.PILL_H );
    const pill_cy = h / 2 - pill_h / 2 - LayoutScaler.heightFromDesign( TournamentMenuProgress.PILL_BOTTOM_PAD );
    const pad = LayoutScaler.widthFromDesign( TournamentMenuProgress.PILL_PAD );
    const inner_h = pill_h - pad * 2;

    // mini_pb_bg carries its drop shadow around the white capsule: 15px on each
    // side of a 50px-high capsule in the 250x80 art.
    const shadow = pill_h * 15 / 50;
    this.drawCapsuleArt( ImgID.UI_TournamentMiniPbBg, "tournament_mini_pb_bg", 45 / 80,
      left - shadow, pill_cy - pill_h / 2 - shadow, w + shadow * 2, pill_h + shadow * 2 );

    const track_left = left + pad;
    const track_right = left + w - pad;
    this.drawCapsuleArt( ImgID.UI_TournamentMiniPbBase, "tournament_mini_pb_base", 22 / 40,
      track_left, pill_cy - inner_h / 2, track_right - track_left, inner_h );

    // The reward icon covers the right end of the track, so the goal the eye
    // sees is its centre: 15 of 30 crowns fills half the way to it.
    const reward_x = track_right - inner_h / 2;
    this.drawFill( track_left, track_right - track_left, this.fill_frac * ( track_right - track_left ), pill_cy, inner_h );

    const reward = this.rewards[this.segment];
    if ( reward && this.fly < 1 )
    {
      const rise = LayoutScaler.heightFromDesign( TournamentMenuProgress.REWARD_RISE
        + TournamentMenuProgress.REWARD_FLY_RISE * easeOutQuad( this.fly ) );
      ctx.save();
      ctx.globalAlpha *= 1 - this.fly;
      this.drawReward( reward, reward_x, pill_cy - rise );
      ctx.restore();
    }

    this.finishRender();
  }

  /**
   * Blue fill over the track, always one shape: a capsule whose rounded right
   * end sits at the fill edge. While the fill is narrower than the track is
   * high, the capsule slides in from the left and the track's rounded left end
   * trims it, so the shape grows smoothly with no switch between forms.
   */
  private drawFill( track_left: number, track_w: number, fill_w: number, cy: number, h: number ): void
  {
    if ( fill_w <= 0 )
      return;

    const ctx = this.ctx;
    const y = cy - h / 2;
    const cap_w = Math.max( fill_w, h );

    ctx.save();
    ctx.beginPath();
    ctx.roundRect( track_left, y, track_w, h, h / 2 );
    ctx.clip();
    fillCapsule( ctx, track_left + fill_w - cap_w, y, cap_w, h, TournamentMenuProgress.FILL_COLOR );
    ctx.restore();
  }


  /** Horizontal three-slice whose caps keep the art's aspect at any height. */
  private drawCapsuleArt( id: ImgID, rule: string, cap_per_h: number, x: number, y: number, w: number, h: number ): void
  {
    const image = ImagesHolder.getLoadedImage( id );
    if ( !image )
      return;
    const cap = Math.min( h * cap_per_h, w / 2 );
    NineSliceDrawer.drawNineSliceImage( this.ctx, image, rule, x, y, w, h, {
      top_left: { width: cap, height: h },
      bottom_right: { width: cap, height: h },
    } );
  }

  private drawReward( reward: TournamentProgressReward, x: number, cy: number ): void
  {
    const icon = TournamentMenuProgress.REWARD_ICON;
    const image = ImagesHolder.getLoadedImage( ADVENTURE_REWARD_ICONS[reward.type] );
    if ( image )
    {
      const iw = LayoutScaler.widthFromDesign( icon );
      const ih = LayoutScaler.heightFromDesign( icon );
      this.ctx.drawImage( image, x - iw / 2, cy - ih / 2, iw, ih );
    }

    const text = reward.type === "coin" ? String( reward.amount ) : rewardAmountText( reward.type, reward.amount );
    const ctx = this.ctx;
    ctx.save();
    ctx.font = `900 ${LayoutScaler.textSizeFromDesign( 36 )}px ${TextStyleScheme.geologica}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.lineJoin = "round";
    ctx.lineWidth = LayoutScaler.textBorderSizeFromDesign( 7 );
    ctx.strokeStyle = ColorScheme.booster_dialog_quantity_outline;
    const ty = cy + LayoutScaler.heightFromDesign( icon * 0.36 );
    const tx = x + LayoutScaler.widthFromDesign( icon * 0.18 );
    ctx.strokeText( text, tx, ty );
    ctx.fillStyle = ColorScheme.white;
    ctx.fillText( text, tx, ty );
    ctx.restore();
  }

  // ── persistence ──────────────────────────────────────────────

  private static seenKey(): string
  {
    return `${TournamentMenuProgress.SEEN_CROWNS_KEY}:${TournamentService.current()?.instance ?? "none"}`;
  }

  private static loadSeenCrowns(): number | null
  {
    try
    {
      const raw = localStorage.getItem( TournamentMenuProgress.seenKey() );
      const value = raw === null ? Number.NaN : Number.parseInt( raw, 10 );
      return Number.isFinite( value ) ? value : null;
    }
    catch
    {
      return null;
    }
  }

  private static saveSeenCrowns( crowns: number ): void
  {
    try { localStorage.setItem( TournamentMenuProgress.seenKey(), String( crowns ) ); }
    catch { /* storage unavailable: the bar just replays next time */ }
  }
}

function fillCapsule( ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, color: string ): void
{
  ctx.beginPath();
  ctx.roundRect( x, y, w, h, Math.min( h / 2, w / 2 ) );
  ctx.fillStyle = color;
  ctx.fill();
}

function easeOutQuad( t: number ): number { return t * ( 2 - t ); }
function easeInOutQuad( t: number ): number { return t < 0.5 ? 2 * t * t : -1 + ( 4 - 2 * t ) * t; }

